import { useState, useEffect } from "react";
import { useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { Search as SearchIcon } from "lucide-react";
import { useApp } from "../context/AppContext";
import ProductCard from "../components/ProductCard";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";

const SearchPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { API } = useApp();
  const [query, setQuery] = useState(searchParams.get("q") || "");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const searchQuery = searchParams.get("q");
    if (searchQuery) {
      setQuery(searchQuery);
      performSearch(searchQuery);
    }
  }, [searchParams]);

  const performSearch = async (searchQuery) => {
    if (!searchQuery.trim()) return;
    
    setLoading(true);
    try {
      const response = await axios.get(`${API}/products?search=${encodeURIComponent(searchQuery)}`);
      setResults(response.data);
    } catch (error) {
      console.error("Search failed:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    if (query.trim()) {
      setSearchParams({ q: query });
    }
  };

  return (
    <div className="min-h-screen py-8" data-testid="search-page">
      <div className="container mx-auto px-4 md:px-6 lg:px-8">
        <div className="max-w-2xl mx-auto mb-8">
          <form onSubmit={handleSearch} className="relative">
            <Input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search for products..."
              className="w-full h-14 pl-12 pr-4 text-lg"
              data-testid="search-input"
            />
            <SearchIcon className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
            <Button
              type="submit"
              className="absolute right-2 top-1/2 -translate-y-1/2"
              data-testid="search-button"
            >
              Search
            </Button>
          </form>
        </div>

        {searchParams.get("q") && (
          <div className="mb-6">
            <p className="text-muted-foreground" data-testid="search-results-count">
              {loading ? "Searching..." : `${results.length} results for "${searchParams.get("q")}"`}
            </p>
          </div>
        )}

        {loading ? (
          <div className="product-grid">
            {[...Array(8)].map((_, i) => (
              <div key={i} className="space-y-3">
                <div className="aspect-[4/5] bg-muted rounded-xl animate-pulse" />
                <div className="h-4 bg-muted rounded w-3/4 animate-pulse" />
                <div className="h-6 bg-muted rounded w-1/2 animate-pulse" />
              </div>
            ))}
          </div>
        ) : results.length > 0 ? (
          <div className="product-grid">
            {results.map((product) => (
              <ProductCard key={product.id} product={product} />
            ))}
          </div>
        ) : searchParams.get("q") ? (
          <div className="text-center py-20">
            <div className="w-24 h-24 bg-muted rounded-full flex items-center justify-center mx-auto mb-6">
              <SearchIcon className="w-10 h-10 text-muted-foreground" />
            </div>
            <h3 className="text-xl font-semibold mb-2">No products found</h3>
            <p className="text-muted-foreground mb-6">
              Try different keywords or browse our categories
            </p>
            <Link to="/categories">
              <Button data-testid="browse-categories">Browse Categories</Button>
            </Link>
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default SearchPage;
